import { useState, useCallback } from 'react';

type SortDirection = 'asc' | 'desc';

interface UseTableSortReturn<T> {
  sortColumn: string | null;
  sortDirection: SortDirection;
  handleSort: (column: string) => void;
  sortData: (data: T[], accessors: Record<string, (item: T) => unknown>) => T[];
}

export function useTableSort<T>(
  onSortChange?: () => void,
  initialSort?: { column: string; direction?: SortDirection }
): UseTableSortReturn<T> {
  const [sortColumn, setSortColumn] = useState<string | null>(initialSort?.column ?? null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort?.direction ?? 'asc');

  const handleSort = useCallback(
    (column: string) => {
      setSortColumn(prev => {
        if (prev === column) {
          setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
          setSortDirection('asc');
        }
        return column;
      });
      onSortChange?.();
    },
    [onSortChange]
  );

  const sortData = useCallback(
    (data: T[], accessors: Record<string, (item: T) => unknown>): T[] => {
      if (!sortColumn || !accessors[sortColumn]) return data;

      const accessor = accessors[sortColumn];
      return [...data].sort((a, b) => {
        const aVal = accessor(a);
        const bVal = accessor(b);

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        let comparison = 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
          comparison = aVal === bVal ? 0 : aVal ? -1 : 1;
        } else {
          comparison = String(aVal).localeCompare(String(bVal));
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    },
    [sortColumn, sortDirection]
  );

  return { sortColumn, sortDirection, handleSort, sortData };
}
